(function () {
  'use strict';

  const DESKTOP = 1024;
  const DISMISS_THRESHOLD = 0.25;
  const VELOCITY_THRESHOLD = 0.5;
  const ACTIVE_CLASS = 'sidebar-wrapper--active';
  const BODY_ACTIVE_CLASS = 'sidebar-open';
  const HTML_ACTIVE_CLASS = 'body-sidebar-open';
  const BODY_CLOSE_DELAY = 400;
  const ACTIONS_CALCULATION_CLASS = 'sidebar-actions-calculation';

  let bodyCloseTimer = 0;

  /**
   * @param {HTMLElement} el
   * @param {HTMLElement} sidebarPanel
   * @returns {HTMLElement | null}
   */
  function getScrollableParent(el, sidebarPanel) {
    let node = el instanceof HTMLElement ? el : el.parentElement;
    while (node && node !== sidebarPanel) {
      if (node.scrollHeight > node.clientHeight && node.scrollTop > 0) return node;
      node = node.parentElement;
    }
    return null;
  }

  function syncLockScroll() {
    const navOpen = document.querySelector('.nav__wrapper.nav__active');
    const sidebarOpen = document.querySelector(`.sidebar-wrapper.${ACTIVE_CLASS}`);
    if (!navOpen && !sidebarOpen) {
      document.body.classList.remove('lock-scroll');
    }
  }

  function hasActiveSidebar() {
    return Boolean(document.querySelector(`.sidebar-wrapper.${ACTIVE_CLASS}`));
  }

  /**
   * @param {HTMLElement} wrapper
   */
  function syncSidebarActionsHeight(wrapper) {
    const sidebarBody = wrapper.querySelector('.sidebar-body');
    if (!(sidebarBody instanceof HTMLElement)) return;

    const sidebarActions = wrapper.querySelector('.sidebar-actions');
    if (!(sidebarActions instanceof HTMLElement) || !sidebarActions.classList.contains(ACTIONS_CALCULATION_CLASS)) {
      sidebarBody.style.removeProperty('--bottom-distance');
      return;
    }

    sidebarBody.style.setProperty('--bottom-distance', `${sidebarActions.getBoundingClientRect().height}px`);
  }

  function setBodySidebarOpen() {
    window.clearTimeout(bodyCloseTimer);
    document.body.classList.add(BODY_ACTIVE_CLASS);
    document.documentElement.classList.add(HTML_ACTIVE_CLASS);
  }

  function scheduleBodySidebarClose() {
    if (hasActiveSidebar()) return;

    window.clearTimeout(bodyCloseTimer);
    bodyCloseTimer = window.setTimeout(() => {
      if (!hasActiveSidebar()) {
        document.body.classList.remove(BODY_ACTIVE_CLASS);
        document.documentElement.classList.remove(HTML_ACTIVE_CLASS);
      }
    }, BODY_CLOSE_DELAY);
  }

  /**
   * @param {HTMLElement} wrapper
   */
  function closeSidebar(wrapper) {
    wrapper.classList.remove(ACTIVE_CLASS);
    wrapper.dispatchEvent(new CustomEvent('sidebar:close', { bubbles: true }));
    scheduleBodySidebarClose();

    syncLockScroll();
  }

  /**
   * @param {HTMLElement} wrapper
   * @param {HTMLElement} sidebarPanel
   */
  function bindSidebarSwipe(wrapper, sidebarPanel) {
    if (wrapper.dataset.ucSidebarSwipeBound === 'true') return;
    wrapper.dataset.ucSidebarSwipeBound = 'true';

    let touchStartY = 0;
    let touchCurrentY = 0;
    let isDragging = false;
    let touchStartTime = 0;

    const onTouchStart = (e) => {
      if (window.innerWidth >= DESKTOP) return;
      if (!wrapper.classList.contains(ACTIVE_CLASS)) return;
      if (!e.touches[0]) return;
      if (getScrollableParent(e.target, sidebarPanel)) return;

      touchStartY = e.touches[0].clientY;
      touchCurrentY = touchStartY;
      touchStartTime = Date.now();
      isDragging = false;
    };

    const onTouchMove = (e) => {
      if (window.innerWidth >= DESKTOP) return;
      if (!wrapper.classList.contains(ACTIVE_CLASS) || !e.touches[0]) return;

      const clientY = e.touches[0].clientY;
      const deltaY = clientY - touchStartY;

      if (!isDragging) {
        if (deltaY <= 0) return;
        isDragging = true;
        sidebarPanel.style.transition = 'none';
      }

      if (isDragging) e.preventDefault();

      touchCurrentY = clientY;
      const clampedDelta = Math.max(0, touchCurrentY - touchStartY);
      sidebarPanel.style.transform = `translateY(${clampedDelta}px)`;
    };

    const resetDragVisual = () => {
      sidebarPanel.style.transition = '';
      sidebarPanel.style.transform = '';
    };

    const onTouchEnd = () => {
      if (!isDragging) return;
      isDragging = false;

      const deltaY = touchCurrentY - touchStartY;
      const elapsed = Math.max(Date.now() - touchStartTime, 1);
      const velocity = deltaY / elapsed;
      const panelHeight = sidebarPanel.offsetHeight;
      const shouldDismiss = deltaY > panelHeight * DISMISS_THRESHOLD || velocity > VELOCITY_THRESHOLD;

      resetDragVisual();

      if (shouldDismiss) {
        closeSidebar(wrapper);
      }
    };

    sidebarPanel.addEventListener('touchstart', onTouchStart, { passive: true });
    sidebarPanel.addEventListener('touchmove', onTouchMove, { passive: false });
    sidebarPanel.addEventListener('touchend', onTouchEnd);
    sidebarPanel.addEventListener('touchcancel', onTouchEnd);
  }

  /**
   * @param {HTMLElement} wrapper
   */
  function bindWrapper(wrapper) {
    const sidebarPanel = wrapper.querySelector(':scope > .sidebar');
    if (!(sidebarPanel instanceof HTMLElement)) return;

    syncSidebarActionsHeight(wrapper);
    bindSidebarSwipe(wrapper, sidebarPanel);
  }

  function hydrate() {
    const wrappers = Array.from(document.querySelectorAll('.sidebar-wrapper'));
    for (const wrapper of wrappers) {
      bindWrapper(wrapper);
    }
  }

  function openSidebar(wrapper) {
    syncSidebarActionsHeight(wrapper);
    wrapper.classList.add(ACTIVE_CLASS);
    setBodySidebarOpen();
    document.body.classList.add('lock-scroll');
  }

  /**
   * Sets the open state of an `accordion-custom` element and keeps its summary's
   * `aria-expanded` in sync.
   * @param {HTMLElement} accordion
   * @param {boolean} open
   */
  function setAccordionOpen(accordion, open) {
    const details = accordion.querySelector(':scope > details, details');
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = open;
    const summary = details.querySelector('summary');
    if (summary instanceof HTMLElement) summary.setAttribute('aria-expanded', String(open));
  }

  /**
   * Toggles the accordions inside `.facets__form`, treating the accordion nested
   * inside `<sorting-filter-component>` opposite to the filter accordions.
   * @param {HTMLElement} wrapper
   * @param {'filters' | 'sorting'} focus - Which group to expand (the other collapses).
   */
  function syncFilterAccordions(wrapper, focus) {
    const form = wrapper.querySelector('.facets__form');
    if (!(form instanceof HTMLElement)) return;

    for (const accordion of form.querySelectorAll('accordion-custom')) {
      if (!(accordion instanceof HTMLElement)) continue;
      const inSorting = Boolean(accordion.closest('sorting-filter-component'));
      const shouldOpen = focus === 'sorting' ? inSorting : !inSorting;
      setAccordionOpen(accordion, shouldOpen);
    }
  }

  /**
   * @param {string} handle
   * @returns {HTMLElement | undefined}
   */
  function findSidebarByHandle(handle) {
    const sidebar = document.querySelector(`[data-sidebar-body="${CSS.escape(handle)}"]`);
    return sidebar instanceof HTMLElement && sidebar.classList.contains('sidebar-wrapper') ? sidebar : undefined;
  }

  function onDocumentClick(e) {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;

    const drawerTrigger = target.closest('[data-open-drawer], [data-uc-sidebar-trigger]');
    if (drawerTrigger instanceof HTMLElement) {
      const rawHandle =
        drawerTrigger.dataset.openDrawer ?? drawerTrigger.getAttribute('data-uc-sidebar-trigger')?.replace(/^#/, '');
      if (!rawHandle) return;

      e.preventDefault();
      e.stopPropagation();

      const sidebar = rawHandle.startsWith('#')
        ? document.querySelector(rawHandle)
        : findSidebarByHandle(rawHandle);

      if (sidebar instanceof HTMLElement && sidebar.classList.contains('sidebar-wrapper')) {
        openSidebar(sidebar);
      }
      return;
    }

    const openBtn = target.closest('button[data-uc-sidebar-target]');
    if (openBtn instanceof HTMLButtonElement) {
      const sel = openBtn.getAttribute('data-uc-sidebar-target');
      if (!sel) return;
      const next = document.querySelector(sel);
      if (next instanceof HTMLElement && next.classList.contains('sidebar-wrapper')) {
        openSidebar(next);
        if (openBtn.classList.contains('collection-filter-btn')) {
          syncFilterAccordions(next, 'filters');
        } else if (openBtn.classList.contains('collection-sort-btn')) {
          syncFilterAccordions(next, 'sorting');
        }
      }
      return;
    }

    const closeBtn = target.closest('.sidebar-close');
    if (closeBtn) {
      const wrapper = closeBtn.closest('.sidebar-wrapper');
      if (wrapper instanceof HTMLElement && wrapper.classList.contains(ACTIVE_CLASS)) {
        closeSidebar(wrapper);
      }
      return;
    }

    const bg = target.closest('.sidebar-background');
    if (bg) {
      const wrapper = bg.closest('.sidebar-wrapper');
      if (wrapper instanceof HTMLElement && wrapper.classList.contains(ACTIVE_CLASS)) {
        closeSidebar(wrapper);
      }
    }
  }

  function init() {
    hydrate();

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKeydown);
    window.addEventListener('resize', hydrate);

    const observer = new MutationObserver(() => hydrate());
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * @param {KeyboardEvent} e
   */
  function onDocumentKeydown(e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;

    const target = e.target instanceof Element ? e.target.closest('[data-open-drawer][role="button"]') : null;
    if (!(target instanceof HTMLElement)) return;

    e.preventDefault();

    const sidebar = findSidebarByHandle(target.dataset.openDrawer ?? '');
    if (sidebar) openSidebar(sidebar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
